/**
 * Report a handled edge-function / Supabase failure to Sentry.
 *
 * Why this exists: a 403/500 from an edge function does NOT throw — fetch()
 * resolves on any HTTP status and supabase.functions.invoke() returns
 * { data, error }. Call sites handle those gracefully (toast, error return), so
 * Sentry's global handlers never see them. Without an explicit report, real
 * production failures are invisible. Route every edge/invoke error branch
 * through this so they surface in the site-manager project.
 *
 * User-cancelled requests (AbortController) are NOT failures — pass aborted:true
 * (or an AbortError) and they are dropped.
 *
 * Org/user context is already attached to the Sentry scope on auth (see
 * useAuth), so callers only need endpoint + status + the error.
 */
import * as Sentry from "@sentry/react";

export interface EdgeFailure {
  /** The function name or path, e.g. "client-dashboard-agent". */
  endpoint: string;
  /** HTTP status when there was a response; omit for network/thrown errors. */
  status?: number;
  /** The underlying error or server-provided message. */
  error?: unknown;
  /** True when the request was user-cancelled — skips reporting. */
  aborted?: boolean;
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}

/** supabase.functions.invoke() rejects with a FunctionsHttpError that carries
 *  the Response on `.context`; pull the HTTP status off it when present. */
function deriveStatus(error: unknown): number | undefined {
  const ctx = (error as { context?: { status?: unknown } } | null | undefined)?.context;
  return typeof ctx?.status === "number" ? ctx.status : undefined;
}

export function captureEdgeFailure({ endpoint, status, error, aborted }: EdgeFailure): void {
  if (aborted || isAbort(error)) return;

  const resolvedStatus = status ?? deriveStatus(error);
  // 4xx = expected/handleable (warning); 5xx or network = warning-worthy error.
  const statusLabel = resolvedStatus !== undefined ? String(resolvedStatus) : "network";
  const level: Sentry.SeverityLevel =
    resolvedStatus !== undefined && resolvedStatus < 500 ? "warning" : "error";
  const message =
    error instanceof Error ? error.message : typeof error === "string" ? error : undefined;

  Sentry.captureMessage(`Edge function ${endpoint} failed (${statusLabel})`, {
    level,
    tags: { edge_endpoint: endpoint, edge_status: statusLabel },
    // Group by endpoint + status so repeated 403s on the same function collapse
    // into one issue instead of flooding.
    fingerprint: ["edge-failure", endpoint, statusLabel],
    extra: { serverMessage: message },
  });
}
